import {
  formatMarketTime,
  getSingleDayHourlyTickDates,
  isSameCalendarDay,
} from '@helpers';
import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import styles from './MultiLineChartAxes.module.scss';

export type MultiLineChartAxesProps = {
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  yAxisFormatter: (n: number) => string;
};

const createMonthlyAxis = (xScale: d3.ScaleTime<number, number>) =>
  d3
    .axisBottom<Date>(xScale)
    .ticks(d3.timeMonth.every(1))
    .tickSize(0)
    .tickPadding(16)
    .tickFormat(d3.timeFormat('%b'));

const createIntradayAxis = (
  xScale: d3.ScaleTime<number, number>,
  startDate: Date,
  endDate: Date
) => {
  const tickValues = getSingleDayHourlyTickDates(startDate, endDate);
  if (tickValues.length === 0) {
    return d3
      .axisBottom<Date>(xScale)
      .tickSize(0)
      .tickPadding(16)
      .tickValues([]);
  }

  const lastTickTimestamp = tickValues[tickValues.length - 1].getTime();

  return d3
    .axisBottom<Date>(xScale)
    .tickSize(0)
    .tickPadding(16)
    .tickValues(tickValues)
    .tickFormat((value: Date) => {
      const suffix = value.getTime() === lastTickTimestamp ? ' ET' : '';
      return `${formatMarketTime(value)}${suffix}`;
    });
};

export const MultiLineChartAxes = ({
  xScale,
  yScale,
  yAxisFormatter,
}: MultiLineChartAxesProps) => {
  const xAxisRef = useRef<SVGGElement>(null);
  const yAxisRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (xAxisRef.current && xScale.range()[1] > 0) {
      const axisSelection = d3.select(xAxisRef.current);
      const [domainStart, domainEnd] = xScale.domain();
      const axis = isSameCalendarDay(domainStart, domainEnd)
        ? createIntradayAxis(xScale, domainStart, domainEnd)
        : createMonthlyAxis(xScale);

      axisSelection.call(axis);
    }

    if (yAxisRef.current && yScale.range()[0] > 0) {
      const axisSelection = d3.select(yAxisRef.current);
      const axis = d3
        .axisRight<number>(yScale)
        .ticks(6)
        .tickSize(xScale.range()[1])
        .tickFormat(yAxisFormatter)
        .tickPadding(10);

      axisSelection.call(axis).call(g => g.select('.domain').remove());
    }
  }, [xScale, yScale, yAxisFormatter]);

  return (
    <g>
      <g
        className={styles.chartAxisG}
        ref={xAxisRef}
        transform={`translate(0, ${yScale.range()[0]})`}
        data-testid="chart-x-axis-group"
      />
      <g className={styles.chartAxisG} ref={yAxisRef} />
    </g>
  );
};
